import { useState } from 'react';
import { useSearch } from '@tanstack/react-router';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui';

/**
 * Two modes in one page:
 * - with a `?token=`, it's the set-a-new-password form (reset + initial setup)
 * - without, it's the request-a-link form
 *
 * The migrated users all arrive here first (no password until they set one),
 * so this doubles as the account-activation page.
 */
export function ResetPage() {
  const { token } = useSearch({ from: '/reset' });
  return token ? <SetPasswordForm token={token} /> : <RequestLinkForm />;
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-white px-4 dark:bg-shell-900">
      <div className="w-full max-w-[420px]">
        <div className="rounded-2xl bg-white p-10 text-gray-900 shadow-[0_8px_32px_rgba(0,0,0,0.18)]">
          <img src="/ecotone-logo.jpeg" alt="Ecotone" className="mx-auto mb-8 block w-[180px]" />
          {children}
        </div>
      </div>
    </div>
  );
}

function RequestLinkForm() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    // Always succeeds from the client's view — the server never reveals whether
    // the address exists, so we show the same confirmation either way.
    await api.requestReset(email).catch(() => {});
    setSent(true);
    setBusy(false);
  }

  if (sent) {
    return (
      <Shell>
        <p className="text-sm text-body">
          Falls ein Konto mit dieser E-Mail existiert, wurde ein Link zum Festlegen des Passworts
          gesendet. Bitte den Posteingang prüfen.
        </p>
        <a href="/login" className="mt-4 inline-block text-sm text-brand-500 hover:underline">
          Zurück zur Anmeldung
        </a>
      </Shell>
    );
  }

  return (
    <Shell>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <h2 className="text-sm font-semibold text-strong">Passwort festlegen</h2>
          <p className="mt-1 text-[13px] text-muted">
            E-Mail-Adresse eingeben — wir senden einen Link zum Festlegen des Passworts.
          </p>
        </div>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoFocus
          autoComplete="email"
          placeholder="name@ecotone.bio"
          className="w-full rounded-lg border line surface-card px-3 py-2 text-sm text-strong
                     placeholder:text-faint focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
        <Button type="submit" variant="primary" loading={busy} className="w-full">
          Link senden
        </Button>
        <a href="/login" className="block text-center text-xs text-muted hover:text-strong">
          Zurück zur Anmeldung
        </a>
      </form>
    </Shell>
  );
}

function SetPasswordForm({ token }: { token: string }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 10) {
      setError('Das Passwort muss mindestens 10 Zeichen lang sein.');
      return;
    }
    if (password !== confirm) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }
    setBusy(true);
    try {
      await api.resetPassword(token, password);
      // Password set; send them to login to sign in with it.
      window.location.assign('/login?reset=success');
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Der Link ist ungültig oder abgelaufen.',
      );
      setBusy(false);
    }
  }

  return (
    <Shell>
      <form onSubmit={submit} className="space-y-4">
        <h2 className="text-sm font-semibold text-strong">Neues Passwort festlegen</h2>
        <label className="block">
          <span className="mb-1 block text-[13px] font-medium text-body">Passwort</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoFocus
            autoComplete="new-password"
            className="w-full rounded-lg border line surface-card px-3 py-2 text-sm text-strong
                       focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-[13px] font-medium text-body">Passwort bestätigen</span>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            autoComplete="new-password"
            className="w-full rounded-lg border line surface-card px-3 py-2 text-sm text-strong
                       focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </label>
        <p className="text-xs text-faint">Mindestens 10 Zeichen.</p>
        {error && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-700 dark:bg-red-950/40 dark:text-red-300" role="alert">
            {error}
          </p>
        )}
        <Button type="submit" variant="primary" loading={busy} className="w-full">
          Passwort speichern
        </Button>
      </form>
    </Shell>
  );
}
